import React, { useState, useEffect } from 'react';
import { X, Upload, Mail, Phone, Calendar, User, Home, Euro, FileText } from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ownerDisplay, resolveAssociationName } from '../utils/applicationOwner';

export default function ApplicationDetailModal({ application, isOpen, onClose }) {
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [brfInfo, setBrfInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // Hämta ägarinformation och BRF-info när modal öppnas
  useEffect(() => {
    if (isOpen && application) {
      fetchApplicationDetails();
    } else if (!isOpen) {
      // Rensa state när modal stängs
      setOwnerInfo(null);
      setBrfInfo(null);
      setLoading(false);
    }
  }, [isOpen, application]);

  const fetchApplicationDetails = async () => {
    setLoading(true);
    try {
      // Hämta ägarinformation från users-collection
      if (application.createdBy) {
        const ownerDoc = await getDoc(doc(db, 'users', application.createdBy));
        if (ownerDoc.exists()) {
          setOwnerInfo(ownerDoc.data());
        }
      }

      // Hämta BRF-information från associations-collection
      if (application.associationId) {
        const brfDoc = await getDoc(doc(db, 'associations', application.associationId));
        if (brfDoc.exists()) {
          setBrfInfo(brfDoc.data());
        }
      } else if (application.housingCooperative) {
        // Fallback: Sök efter BRF baserat på namn
        const associationsRef = collection(db, 'associations');
        const q = query(associationsRef, where('name', '==', application.housingCooperative));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setBrfInfo(querySnapshot.docs[0].data());
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !application) return null;

  // Föreningens kommentarer vid avslag, i den ordning de fattades.
  const text = (value) => (typeof value === 'string' ? value.trim() : '');
  const decisionNotes = [
    text(application.rejectionReason) && {
      label: 'Avslag i första granskningen',
      text: text(application.rejectionReason),
      by: application.reviewedByName || application.reviewedBy,
      at: application.reviewedAt,
    },
    text(application.finalRejectionReason) && {
      label: 'Slutligt avslag',
      text: text(application.finalRejectionReason),
      by: application.finalDecisionByName || application.finalApprovedBy,
      at: application.finalApprovedAt,
    },
  ].filter(Boolean);

  // Beräkna process-info (temporär logik)
  const getProcessInfo = () => {
    const createdDate = new Date(application.createdAt || Date.now());
    const daysWaiting = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    
    let currentStep = 1;
    let stepDates = {
      created: application.createdAt ? new Date(application.createdAt).toLocaleDateString('sv-SE') : '',
      brfApproved: '',
      tenantInfo: '',
      creditCheck: '',
      finalApproved: ''
    };

    switch (application.status) {
      case 'PENDING':
        currentStep = 1;
        break;
      case 'APPROVED':
        currentStep = 2;
        stepDates.brfApproved = new Date().toLocaleDateString('sv-SE'); // Approximation
        break;
      case 'TENANT_INFO_SUBMITTED':
        currentStep = 3;
        stepDates.brfApproved = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toLocaleDateString('sv-SE');
        stepDates.tenantInfo = new Date().toLocaleDateString('sv-SE');
        break;
      case 'CREDIT_CHECK_COMPLETE':
        currentStep = 4;
        break;
      case 'FINAL_APPROVED':
      case 'ACTIVE':
        currentStep = 5;
        break;
    }

    return { currentStep, stepDates, daysWaiting };
  };

  const { currentStep, stepDates, daysWaiting } = getProcessInfo();

  // Formatera datum för tidslinje
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate()}/${date.getMonth() + 1} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Process steps
  const steps = [
    { label: 'Skapad', completed: currentStep >= 1, date: formatDate(stepDates.created) },
    { label: 'BRF', completed: currentStep >= 2, date: formatDate(stepDates.brfApproved) },
    { label: 'Hyresgäst', completed: currentStep >= 3, date: formatDate(stepDates.tenantInfo) },
    { label: 'Kredit', completed: currentStep >= 4, date: '' },
    { label: 'Slutgodkänt', completed: currentStep >= 5, date: '' }
  ];

  // Status-meddelande baserat på nuvarande steg
  const getStatusMessage = () => {
    switch (application.status) {
      case 'PENDING':
        return {
          color: 'text-yellow-800 bg-yellow-100',
          icon: '🟡',
          title: 'VÄNTAR PÅ BRF',
          message: 'BRF ska granska och godkänna/avslå ansökan'
        };
      case 'APPROVED':
        return {
          color: 'text-blue-800 bg-blue-100',
          icon: '🔵',
          title: 'VÄNTAR PÅ ÄGARE',
          message: 'Ägare ska fylla i hyresgästuppgifter'
        };
      case 'TENANT_INFO_SUBMITTED':
        return {
          color: 'text-orange-800 bg-orange-100',
          icon: '🟠',
          title: 'KREDITKONTROLL KRÄVS',
          message: 'Hyria ska utföra kreditkontroll för hyresgästen'
        };
      case 'CREDIT_CHECK_COMPLETE':
        return {
          color: 'text-purple-800 bg-purple-100',
          icon: '🟣',
          title: 'VÄNTAR PÅ SLUTBESLUT',
          message: 'BRF ska fatta slutgiltigt beslut'
        };
      case 'AWAITING_OWNER_DECISION':
        return {
          color: 'text-indigo-800 bg-indigo-100',
          icon: '📝',
          title: 'VÄNTAR PÅ AVTAL',
          message: 'Bostadsägaren ska skapa och signera avtalet'
        };
      case 'ADDITIONAL_INFO':
        return {
          color: 'text-amber-800 bg-amber-100',
          icon: '📋',
          title: 'KOMPLETTERING KRÄVS',
          message: 'Ytterligare information har begärts'
        };
      case 'FINAL_APPROVED':
      case 'ACTIVE':
        return {
          color: 'text-green-800 bg-green-100',
          icon: '🟢',
          title: 'GODKÄND',
          message: 'Uthyrning är godkänd och aktiv'
        };
      case 'REJECTED':
      case 'OWNER_REJECTED':
      case 'FINAL_REJECTED':
        return {
          color: 'text-red-800 bg-red-100',
          icon: '🔴',
          title: 'AVVISAD',
          message: 'Ansökan har avvisats'
        };
      default:
        return {
          color: 'text-gray-800 bg-gray-100',
          icon: '❓',
          title: 'OKÄND STATUS',
          message: 'Status okänd'
        };
    }
  };

  const statusInfo = getStatusMessage();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Ansökan #{application.id.slice(-6)} - {application.address}, Lgh {application.apartmentNumber}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* BRF & Ägare Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Home className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-gray-900">
                  {resolveAssociationName({ application, association: brfInfo }) || 'Ej angiven'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4" />
                <span>
                  {brfInfo?.boardEmail || application.brfEmail || 'styrelse@karlatornet.se'}
                </span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-blue-600" />
                <span className="font-semibold text-gray-900">
                  {/* Ansökans eget ägarnamn först, sedan kontot. Finns inget underlag
                      står det "Okänd ägare" — förr visades ett påhittat namn här. */}
                  {loading && !ownerInfo ? 'Laddar...' : ownerDisplay({ application, user: ownerInfo }).name} (Ägare)
                </span>
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>
                    {loading && !ownerInfo ? 'Laddar...' : ownerDisplay({ application, user: ownerInfo }).email || '–'}
                  </span>
                </div>
                {ownerInfo?.phoneNumber && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{ownerInfo.phoneNumber}</span>
                  </div>
                )}
                {ownerInfo?.personalNumber && (
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span><strong>Personnummer:</strong> {ownerInfo.personalNumber}</span>
                  </div>
                )}
                {(ownerInfo?.city || ownerInfo?.zipCode) && (
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    <span>
                      {ownerInfo?.zipCode && `${ownerInfo.zipCode} `}
                      {ownerInfo?.city || ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Process Status */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-4">PROCESS-STATUS:</h3>
            
            {/* Compact Progress Bar */}
            <div className="mb-4">
              <div className="relative">
                {/* Background Line */}
                <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200 rounded-full"></div>
                
                {/* Progress Line */}
                <div 
                  className="absolute top-4 left-4 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                  style={{ 
                    width: currentStep === 1 ? '0%' : `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                    maxWidth: 'calc(100% - 2rem)'
                  }}
                ></div>
                
                {/* Steps */}
                <div className="flex items-start justify-between relative">
                  {steps.map((step, index) => {
                    const isCompleted = step.completed;
                    const isCurrent = index === currentStep - 1 && !step.completed;
                    
                    return (
                      <div key={index} className="flex flex-col items-center relative z-10" style={{ flex: '1' }}>
                        {/* Circle */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-all duration-300 ${
                          isCompleted 
                            ? 'bg-green-500 text-white border-green-500' 
                            : isCurrent 
                              ? 'bg-blue-500 text-white border-blue-500' 
                              : 'bg-white text-gray-400 border-gray-300'
                        }`}>
                          {isCompleted ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : isCurrent ? (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          ) : (
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                          )}
                        </div>
                        
                        {/* Label */}
                        <div className="mt-2 text-center">
                          <div className={`text-xs font-medium ${
                            isCompleted ? 'text-green-600' : 
                            isCurrent ? 'text-blue-600' : 
                            'text-gray-500'
                          }`}>
                            {step.label}
                          </div>
                          {step.date && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {step.date}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Current Status */}
            <div className={`p-4 rounded-lg ${statusInfo.color}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{statusInfo.icon}</span>
                <span className="font-bold">{statusInfo.title}</span>
              </div>
              <p className="text-sm">{statusInfo.message}</p>

              {/* Detaljerad anledning när status ändrats manuellt (t.ex.
                  ADDITIONAL_INFO eller manuella status-byten). Visar texten
                  som BRF/Hyria-admin angav när statusen sattes, plus när
                  och av vem — så att support inte behöver gissa. */}
              {application.lastStatusChangeReason && (
                <div className="mt-3 pt-3 border-t border-current/20 text-xs space-y-1">
                  <div>
                    <span className="font-semibold">Anledning:</span>{' '}
                    {application.lastStatusChangeReason}
                  </div>
                  {application.lastStatusChangeAt && (
                    <div className="opacity-75">
                      Ändrad {new Date(application.lastStatusChangeAt).toLocaleString('sv-SE')}
                      {application.lastStatusChangeBy && ` av ${application.lastStatusChangeBy}`}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Ägarens skäl — vid "Annan anledning" är det ägarens egen text. */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              SKÄL TILL UTHYRNING:
            </h3>
            {application.reason && String(application.reason).trim() ? (
              <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-200">
                {application.reason}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Inget skäl angivet</div>
            )}
          </div>

          {/* Föreningens kommentarer vid avslag — så att Hyria kan hjälpa
              föreningen om något blivit fel. */}
          {decisionNotes.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                FÖRENINGENS BESLUT:
              </h3>
              <div className="space-y-3">
                {decisionNotes.map((note) => (
                  <div key={note.label} className="text-sm bg-red-50 rounded-lg p-3 border border-red-200">
                    <div className="font-semibold text-red-800">{note.label}</div>
                    <div className="mt-1 text-gray-800 whitespace-pre-wrap">{note.text}</div>
                    {(note.by || note.at) && (
                      <div className="mt-1 text-xs text-gray-500">
                        {[note.by, note.at && new Date(note.at).toLocaleString('sv-SE')].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hyresgäst Info */}
          {(application.tenantFirstName || application.tenantLastName) && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                HYRESGÄST:
              </h3>
              <div className="space-y-2 text-sm">
                <div>• <strong>Namn:</strong> {application.tenantFirstName} {application.tenantLastName}</div>
                <div>• <strong>Telefon:</strong> {application.tenantPhoneNumber || 'Ej angivet'}</div>
                <div>• <strong>Email:</strong> {application.tenantEmail || 'Ej angivet'}</div>
                <div>• <strong>Personnummer:</strong> {application.tenantPersonalNumber || 'Ej angivet'}</div>
              </div>
            </div>
          )}

          {/* Ekonomiska villkor */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Euro className="w-4 h-4" />
              EKONOMISKA VILLKOR:
            </h3>
            <div className="space-y-2 text-sm">
              <div>• <strong>Hyra:</strong> {(application.rentalFee || application.rent) ? `${(application.rentalFee || application.rent).toLocaleString()} kr/mån` : 'Ej angivet'}</div>
              <div>• <strong>Deposition:</strong> {application.deposit ? `${application.deposit.toLocaleString()} kr` : 'Ej angivet'}</div>
              {application.clearingnummer && (
                <div>• <strong>Clearingnummer:</strong> {application.clearingnummer}</div>
              )}
              {application.bankAccountNumber && (
                <div>• <strong>Bankkonto:</strong> {application.bankAccountNumber}</div>
              )}
              <div>• <strong>Tillåten uthyrningsperiod (BRF-godkänd):</strong> {application.fromDate} → {application.toDate}</div>
              {(application.contractStartDate && application.contractEndDate) && (
                <div>• <strong>Avtalstid:</strong> {application.contractStartDate} → {application.contractEndDate}</div>
              )}
            </div>
          </div>

          {/* Lägenhetsinformation */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Home className="w-4 h-4" />
              LÄGENHETSINFORMATION:
            </h3>
            <div className="space-y-2 text-sm">
              <div>• <strong>Rum:</strong> {application.rooms ? `${application.rooms} rum` : 'Ej angivet'}</div>
              <div>• <strong>Area:</strong> {application.squareMeter ? `${application.squareMeter} m²` : 'Ej angivet'}</div>
              <div>• <strong>Våning:</strong> {application.floor || 'Ej angivet'}</div>
              {(application.heatingIncluded || application.hotWaterIncluded || application.electricityIncluded ||
                application.internetIncluded || application.parkingIncluded || application.furnitureIncluded) && (
                <div>
                  <strong>Ingår i hyran:</strong>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {application.heatingIncluded && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        Värme
                      </span>
                    )}
                    {application.hotWaterIncluded && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        Varmvatten
                      </span>
                    )}
                    {application.electricityIncluded && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        El
                      </span>
                    )}
                    {application.internetIncluded && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        Internet
                      </span>
                    )}
                    {application.parkingIncluded && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        Parkering
                      </span>
                    )}
                    {application.furnitureIncluded && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        Möbler
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Övrigt — fritextfältet som ägaren fyller i på steg 2.
              Visas bara om det faktiskt finns innehåll. Renderades tidigare
              inte alls i Hyria-admin-vyn, vilket gjorde att information som
              behövdes vid avtalsskrivning saknades. */}
          {application.miscellaneousNotes && application.miscellaneousNotes.trim() && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                ÖVRIGT:
              </h3>
              <div className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-200">
                {application.miscellaneousNotes}
              </div>
            </div>
          )}

          {/* Tidslinje */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              TIDSLINJE:
            </h3>
            <div className="space-y-2 text-sm">
              <div>• {formatDateTime(application.createdAt)} - Ansökan skapad av {
                ownerInfo ?
                  `${ownerInfo.firstName || ''} ${ownerInfo.lastName || ''}`.trim() || 'ägare' :
                  'ägare'
              }</div>
              {application.brfApprovedAt && (
                <div>• {formatDateTime(application.brfApprovedAt)} - BRF godkände ansökan</div>
              )}
              {application.tenantInfoSubmittedAt && (
                <div>• {formatDateTime(application.tenantInfoSubmittedAt)} - Hyresgästuppgifter ifyllda</div>
              )}
              {application.creditCheckDate && (
                <div>• {formatDateTime(application.creditCheckDate)} - Kreditkontroll genomförd</div>
              )}
              {application.finalApprovedAt && (
                <div>• {formatDateTime(application.finalApprovedAt)} - Slutligt godkännande av BRF</div>
              )}
              {!['FINAL_APPROVED', 'ACTIVE'].includes(application.status) && (
                <div className="font-medium text-orange-600">
                  • Nu - Väntar på {statusInfo.title.toLowerCase()} ({daysWaiting} dagar)
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
            {application.status === 'TENANT_INFO_SUBMITTED' && (
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                <Upload className="w-4 h-4" />
                Ladda upp kreditrapport
              </button>
            )}
            
            <button className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition">
              <Mail className="w-4 h-4" />
              Skicka påminnelse
            </button>
            
            <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
              <Phone className="w-4 h-4" />
              Kontakta BRF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
